/**
 * DTO Adapters - Convert raw API responses to typed DTOs
 *
 * Uses Valibot validators for runtime safety after a small normalization pass
 * so frontend remains resilient to numeric/string variance in backend payloads.
 */

import type {
  ConfigDto,
  PackageDto,
  PaymentDto,
  SubscriptionDto,
  TariffDto,
  TimeOptionDto,
  TransactionDto,
  UserDto,
} from '@/types/dtos'
import { validators } from '@/utils/validators'

const toRecord = (value: unknown): Record<string, unknown> | null => {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

const toStringValue = (value: unknown, fallback: string = ''): string => {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value)
  }

  return fallback
}

const toOptionalStringValue = (value: unknown): string | undefined => {
  const normalized = toStringValue(value)
  return normalized ? normalized : undefined
}

const toNullableStringValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null
  }

  const normalized = toStringValue(value)
  return normalized ? normalized : null
}

const toNumberValue = (value: unknown, fallback: number = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  return fallback
}

const toOptionalNumberValue = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const parsed = toNumberValue(value, Number.NaN)
  return Number.isFinite(parsed) ? parsed : undefined
}

const toNullableNumberValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = toNumberValue(value, Number.NaN)
  return Number.isFinite(parsed) ? parsed : null
}

const toBooleanValue = (value: unknown, fallback: boolean = false): boolean => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value !== 0
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') {
      return true
    }
    if (normalized === 'false' || normalized === '0') {
      return false
    }
  }

  return fallback
}

const toNumberArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => toOptionalNumberValue(item))
      .filter((item): item is number => item !== undefined)
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown
      return toNumberArray(parsed)
    } catch {
      return []
    }
  }

  return []
}

const toTimeOptionDtoList = (value: unknown): TimeOptionDto[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => {
      const record = toRecord(item)
      if (!record) {
        return null
      }

      const months = toOptionalNumberValue(record.months)
      const price = toOptionalNumberValue(record.price)
      if (months === undefined || price === undefined) {
        return null
      }

      return { months, price }
    })
    .filter((item): item is TimeOptionDto => item !== null)
}

const normalizeTariff = (value: unknown): TariffDto | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const id = toStringValue(record.id)
  if (!id) {
    return null
  }

  return {
    id,
    package_id: toOptionalStringValue(record.package_id),
    duration_months: toOptionalNumberValue(record.duration_months),
    price: toOptionalNumberValue(record.price),
    old_price: toNullableNumberValue(record.old_price),
    discount_percent: toOptionalNumberValue(record.discount_percent),
    is_popular: toBooleanValue(record.is_popular),
    is_active: toBooleanValue(record.is_active, true),
    sort_order: toOptionalNumberValue(record.sort_order),
    device_markups: toNumberArray(record.device_markups),
  }
}

const normalizePackage = (value: unknown): PackageDto | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const id = toStringValue(record.id)
  const name = toStringValue(record.name)
  if (!id || !name) {
    return null
  }

  return {
    id,
    name,
    description: toStringValue(record.description),
    icon: toOptionalStringValue(record.icon),
    is_popular: toBooleanValue(record.is_popular),
    tariffs: Array.isArray(record.tariffs)
      ? record.tariffs
          .map((item) => normalizeTariff(item))
          .filter((item): item is TariffDto => item !== null)
      : [],
  }
}

const normalizeSubscription = (value: unknown): SubscriptionDto | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const id = toStringValue(record.id)
  const configKey = toStringValue(record.config_key)
  const expiresAt = toStringValue(record.expires_at)
  if (!id || !configKey || !expiresAt) {
    return null
  }

  return {
    id,
    config_key: configKey,
    package_name: toStringValue(record.package_name, configKey),
    device_count: Math.max(1, toNumberValue(record.device_count, 1)),
    expires_at: expiresAt,
    is_active: toBooleanValue(record.is_active),
    is_trial: toBooleanValue(record.is_trial),
    access_key: toNullableStringValue(record.access_key),
    country: toNullableStringValue(record.country),
    device_markups: toNumberArray(record.device_markups),
    time_options: toTimeOptionDtoList(record.time_options),
  }
}

const normalizeConfig = (value: unknown): Record<string, unknown> | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const id = toStringValue(record.id)
  const name = toStringValue(record.name)
  if (!id || !name) {
    return null
  }

  return {
    id,
    name,
    price: toNumberValue(record.price),
    duration: toStringValue(record.duration),
    speed_limit: toStringValue(record.speed_limit),
    description: toStringValue(record.description),
  }
}

const normalizePayment = (value: unknown): PaymentDto | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const id = toStringValue(record.id)
  const createdAt = toStringValue(record.created_at)
  if (!id || !createdAt) {
    return null
  }

  const status = toStringValue(record.status, 'pending')
  const normalizedStatus: PaymentDto['status'] =
    status === 'completed' || status === 'failed' ? status : 'pending'

  return {
    id,
    config_id: toOptionalStringValue(record.config_id),
    amount: toNumberValue(record.amount),
    status: normalizedStatus,
    created_at: createdAt,
    completed_at: toOptionalStringValue(record.completed_at),
  }
}

const normalizeTransaction = (value: unknown): TransactionDto | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const id = toStringValue(record.id)
  const userId = toStringValue(record.user_id)
  const createdAt = toStringValue(record.created_at)
  if (!id || !userId || !createdAt) {
    return null
  }

  const type = toStringValue(record.type, 'purchase')
  const normalizedType: TransactionDto['type'] =
    type === 'refund' ||
    type === 'topup' ||
    type === 'admin_add' ||
    type === 'admin_subtract'
      ? type
      : 'purchase'

  return {
    id,
    user_id: userId,
    type: normalizedType,
    amount: toNumberValue(record.amount),
    balance_before: toNumberValue(record.balance_before),
    balance_after: toNumberValue(record.balance_after),
    description: toNullableStringValue(record.description),
    reference_id: toNullableStringValue(record.reference_id),
    created_at: createdAt,
    metadata: toRecord(record.metadata) ?? {},
  }
}

const normalizeUser = (value: unknown): UserDto | null => {
  const record = toRecord(value)
  if (!record) {
    return null
  }

  const telegramId = toStringValue(record.telegramId ?? record.telegram_id)
  if (!telegramId) {
    return null
  }

  const subscriptionStatus = toStringValue(record.subscription_status)
  const normalizedSubscriptionStatus: UserDto['subscription_status'] | undefined =
    subscriptionStatus === 'active' || subscriptionStatus === 'expired' || subscriptionStatus === 'none'
      ? subscriptionStatus
      : undefined

  return {
    id: typeof record.id === 'number' || typeof record.id === 'string' ? record.id : undefined,
    telegramId,
    first_name: toOptionalStringValue(record.first_name),
    last_name: toOptionalStringValue(record.last_name),
    username: toOptionalStringValue(record.username),
    photo_url: toOptionalStringValue(record.photo_url),
    referral_code: toOptionalStringValue(record.referral_code),
    email: toOptionalStringValue(record.email),
    subscription_status: normalizedSubscriptionStatus,
    is_active: record.is_active === undefined ? undefined : toBooleanValue(record.is_active),
    created_at: toOptionalStringValue(record.created_at),
    balance: toOptionalNumberValue(record.balance),
  }
}

/**
 * Package adapters - with Valibot validation
 */
export const toTariffDto = (value: unknown): TariffDto | null => {
  const normalized = normalizeTariff(value)
  if (!normalized) {
    return null
  }

  const result = validators.tariff(normalized)
  return result.success ? (result.data as TariffDto) : null
}

export const toPackageDto = (value: unknown): PackageDto | null => {
  const normalized = normalizePackage(value)
  if (!normalized) {
    return null
  }

  const result = validators.package(normalized)
  return result.success ? (result.data as PackageDto) : null
}

export const toPackageDtoList = (value: unknown): PackageDto[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => toPackageDto(item))
    .filter((item): item is PackageDto => item !== null)
}

/**
 * Subscription adapters
 */
export const toSubscriptionDto = (value: unknown): SubscriptionDto | null => {
  const normalized = normalizeSubscription(value)
  if (!normalized) {
    return null
  }

  const result = validators.subscription(normalized)
  return result.success ? (result.data as SubscriptionDto) : null
}

export const toSubscriptionDtoList = (value: unknown): SubscriptionDto[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => toSubscriptionDto(item))
    .filter((item): item is SubscriptionDto => item !== null)
}

/**
 * Config adapters
 */
export const toConfigDto = (value: unknown): ConfigDto | null => {
  const normalized = normalizeConfig(value)
  if (!normalized) {
    return null
  }

  const result = validators.config(normalized)
  return result.success ? (result.data as ConfigDto) : null
}

export const toConfigDtoList = (value: unknown): ConfigDto[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => toConfigDto(item))
    .filter((item): item is ConfigDto => item !== null)
}

/**
 * Payment adapters
 */
export const toPaymentDto = (value: unknown): PaymentDto | null => {
  const normalized = normalizePayment(value)
  if (!normalized) {
    return null
  }

  const result = validators.payment(normalized)
  return result.success ? (result.data as PaymentDto) : null
}

export const toPaymentDtoList = (value: unknown): PaymentDto[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => toPaymentDto(item))
    .filter((item): item is PaymentDto => item !== null)
}

/**
 * Transaction adapters
 */
export const toTransactionDto = (value: unknown): TransactionDto | null => {
  const normalized = normalizeTransaction(value)
  if (!normalized) {
    return null
  }

  const result = validators.transaction(normalized)
  return result.success ? (result.data as TransactionDto) : null
}

export const toTransactionDtoList = (value: unknown): TransactionDto[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => toTransactionDto(item))
    .filter((item): item is TransactionDto => item !== null)
}

/**
 * User adapters
 */
export const toUserDto = (value: unknown): UserDto | null => {
  const normalized = normalizeUser(value)
  if (!normalized) {
    return null
  }

  const result = validators.user(normalized)
  return result.success ? (result.data as UserDto) : null
}